import { FaUserCircle } from 'react-icons/fa';
import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { transparentize } from 'polished';
import { StaticQuery, graphql, Link } from 'gatsby';
import { scrollbarWidth, scrollbarThumbColor } from '../util/sidebar-config';
import { Z_BELOW_TOP, Z_TOP } from '../util/zIndex';
import { routes } from '../util/routes';
import AuthActions from './auth-actions';
import { AuthorContext } from '../context/author-context';
import { headerHeight } from './header';
import breakpoints from '../util/breakpoints';
import colors from '../util/colors';

const sidebarWidthLarge = 350;
const sidebarWidthSmall = 275;

const Container = styled.aside(props => ({
  flexShrink: 0,
  width: props.collapse ? 0 : sidebarWidthLarge,
  [breakpoints.sm]: {
    width: props.collapse ? 0 : sidebarWidthSmall
  },
  borderRight: `1px solid ${colors.divider}`,
  position: 'relative',
  transition: 'width .3s ease',
  backgroundColor: `#111` || colors.dark,
  zIndex: Z_BELOW_TOP
}));

const ResponsiveContainer = styled(Container)(props => ({
  scrollbarWidth: `none`,
  [`::-webkit-scrollbar`]: {
    width: '0px',
    background: 'black'
  },
  [`::-webkit-scrollbar-thumb`]: {
    background: scrollbarThumbColor
  },
  [`:hover`]: {
    scrollbarWidth: 'auto',
    [`::-webkit-scrollbar`]: {
      width: `${scrollbarWidth}px`
    }
  },
  paddingRight: '0px',
  height: '100%',
  boxShadow: `0 0 48px ${transparentize(0.75, 'black')}`,
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: Z_TOP,
  opacity: props.open ? 1 : 0,
  visibility: props.open ? 'visible' : 'hidden',
  transform: props.open ? 'none' : 'translateX(-100%)',
  transitionProperty: 'transform, opacity, visibility',
  transitionDuration: '150ms',
  transitionTimingFunction: 'ease-in-out'
}));

const Content = styled.div({
  padding: '0px 0px',
  paddingRight: 0
});

const SidebarScroll = styled.div(props => ({
  overflowY: `scroll`,
  overflowX: 'hidden',
  display: `flex`,
  flexDirection: `column`,
  paddingTop: props.paddingTop,
  height: `100%`,
  width: `100%`,
  scrollbarWidth: `none`,
  [`::-webkit-scrollbar`]: {
    width: '4px',
    background: 'transparent'
  },
  [`:hover`]: {
    [`::-webkit-scrollbar`]: {
      width: '4px'
    },
    [`::-webkit-scrollbar-thumb`]: {
      background: `white`
    }
  }
}));

const AuthorArea = styled.div({
  display: `flex`,
  flexDirection: `column`,
  justifyContent: `center`,
  flexWrap: `no-wrap`,
  alignItems: `center`,
  minWidth: `100%`,
  padding: `10px 5px`,
  overflowX: 'hidden',
  textAlign: `left`,
  color: `#aaa`,
  [breakpoints.sm]: {
    textAlign: 'left'
  }
});

const ExistingAuthor = styled.div({
  display: `flex`,
  justifyContent: `space-between`,
  width: `100%`
});

const Sidebar = ({ children, open, hideLogo }) => {
  const { author } = useContext(AuthorContext);

  return (
    <ResponsiveContainer collapse={false} open={open}>
      <SidebarScroll paddingTop={hideLogo ? '0px' : `${headerHeight}px`}>
        <Content>
          <AuthorArea>
            <ExistingAuthor>
              <div
                style={{
                  display: `flex`,
                  alignItems: `center`,
                  justifyContent: `space-between`,
                  width: `100%`
                }}
              >
                {author && (
                  <div>
                    <FaUserCircle style={{marginRight: `7px`, verticalAlign: `middle`}} />
                    <Link to={routes.User(author.name)}>
                      <b>{author.name}</b>
                    </Link>
                  </div>
                )}
                <AuthActions fullWidth cta="Sign In" intent="none" uiSource="sidebar" />
              </div>
            </ExistingAuthor>
          </AuthorArea>
          {children}
        </Content>
      </SidebarScroll>
    </ResponsiveContainer>
  );
};

export default ({ children, open, noLogo, enableCollapse }) => {
  return (
    <StaticQuery
      query={graphql`
        {
          site {
            siteMetadata {
              logoURI
            }
          }
        }
      `}
      render={data => {
        return (
          <Sidebar
            open={open}
            noLogo={noLogo}
            enableCollapse={enableCollapse}
            logoURI={data.site.siteMetadata.logoURI}
          >
            {children}
          </Sidebar>
        );
      }}
    />
  );
};
